import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { CATEGORY_LABELS, CATEGORY_STYLES, CATEGORY_DOT } from '../../utils/statuses';
import { SkeletonList } from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import { totalTravellers } from '../../utils/groupSize';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

export default function TourLeaderHome() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [newTours,     setNewTours]     = useState([]);
  const [legacyTours,  setLegacyTours]  = useState([]);
  const [loadingNew,   setLoadingNew]   = useState(true);
  const [loadingLegacy,setLoadingLegacy] = useState(true);

  const loading = loadingNew || loadingLegacy;
  const tours = [...newTours, ...legacyTours.filter((l) => !newTours.some((n) => n.id === l.id))]
    .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''));

  useEffect(() => {
    if (!currentUser) return;
    // Query both the new tourLeaderIds array and the legacy tourLeaderId field
    const unsubNew = onSnapshot(
      query(collection(db, 'requests'), where('tourLeaderIds', 'array-contains', currentUser.uid)),
      (snap) => { setNewTours(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoadingNew(false); }
    );
    const unsubLegacy = onSnapshot(
      query(collection(db, 'requests'), where('tourLeaderId', '==', currentUser.uid)),
      (snap) => { setLegacyTours(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoadingLegacy(false); }
    );
    return () => { unsubNew(); unsubLegacy(); };
  }, [currentUser]);

  if (loading) return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h2 className="text-[22px] font-bold text-[#111827]">My Tours</h2>
      </div>
      <SkeletonList count={3} />
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h2 className="text-[22px] font-bold text-[#111827]">My Tours</h2>
        <p className="text-[13px] text-[#6B7280] mt-1">Tours you have been assigned as tour leader.</p>
      </div>

      {tours.length === 0 ? (
        <EmptyState
          icon={
            <svg className="w-6 h-6 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
          }
          title="No tours assigned yet"
          description="You'll see your assigned tours here. Contact MST if you believe this is incorrect."
        />
      ) : (
        <div className="space-y-3">
          {tours.map((tour) => {
            const cat = tour.category || 'sales_admin';
            return (
              <button
                key={tour.id}
                onClick={() => navigate(`tours/${tour.id}`)}
                className="w-full text-left bg-white rounded-[12px] border border-[#E5E7EB] p-5 hover:border-pb/30 hover:shadow-sm transition flex items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-[15px] font-bold text-[#111827]">
                      {tour.tourName || `${tour.sport} — ${tour.destination}`}
                    </p>
                    <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold whitespace-nowrap ${CATEGORY_STYLES[cat] || 'bg-[#F3F4F6] text-[#374151]'}`}>
                      <span className={`w-[5px] h-[5px] rounded-full ${CATEGORY_DOT[cat] || 'bg-[#9CA3AF]'}`} />
                      {CATEGORY_LABELS[cat] || cat}
                    </span>
                  </div>
                  <p className="text-[13px] text-[#6B7280]">
                    {tour.tourName && <span>{tour.sport} — {tour.destination} · </span>}
                    {formatDate(tour.departureDate)} – {formatDate(tour.returnDate)}
                    {totalTravellers(tour) ? ` · ${totalTravellers(tour)} travellers` : ''}
                  </p>
                </div>
                <svg className="w-5 h-5 text-[#D1D5DB] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
